/**
 * Board sets — selectable square palettes independent of theme.
 *
 * When `mixMatch` is off in the theme store the board colors come from the
 * active theme's `boardLight` / `boardDark` tokens. When mix-match is on
 * (supporter feature) the chosen board set overrides those CSS variables.
 *
 * Light = the lighter square. Dark = the darker square. Highlight = ring color
 * for selected / target squares.
 */

export type BoardSetId =
  | 'classic'
  | 'walnut'
  | 'marble'
  | 'slate'
  | 'jade'
  | 'bone'
  | 'cinnabar'
  | 'ocean'
  | 'neon'
  | 'parchment';

export type BoardSetKind = 'free' | 'supporter';

export interface BoardSet {
  id: BoardSetId;
  name: string;
  blurb: string;
  kind: BoardSetKind;
  /** rgb triplet "r g b" */
  light: string;
  /** rgb triplet "r g b" */
  dark: string;
  /** rgb triplet "r g b" — selection ring color */
  highlight: string;
}

export const BOARD_SETS: Record<BoardSetId, BoardSet> = {
  classic: {
    id: 'classic',
    name: 'Classic',
    blurb: 'Cream and walnut, traditional pairing.',
    kind: 'supporter',
    light: '232 213 168',
    dark: '138 96 56',
    highlight: '244 196 64',
  },
  walnut: {
    id: 'walnut',
    name: 'Walnut',
    blurb: 'Rich brown grain, tournament warmth.',
    kind: 'supporter',
    light: '210 174 122',
    dark: '94 60 32',
    highlight: '232 162 64',
  },
  marble: {
    id: 'marble',
    name: 'Marble',
    blurb: 'Soft stone, museum-quiet.',
    kind: 'supporter',
    light: '232 230 224',
    dark: '128 124 116',
    highlight: '208 168 88',
  },
  slate: {
    id: 'slate',
    name: 'Slate',
    blurb: 'Cool gray, modern minimal.',
    kind: 'supporter',
    light: '188 198 210',
    dark: '78 90 108',
    highlight: '120 168 220',
  },
  jade: {
    id: 'jade',
    name: 'Jade',
    blurb: 'Translucent green lacquer.',
    kind: 'supporter',
    light: '198 224 196',
    dark: '60 110 84',
    highlight: '220 196 88',
  },
  bone: {
    id: 'bone',
    name: 'Bone',
    blurb: 'Carved ivory and sepia.',
    kind: 'supporter',
    light: '232 220 192',
    dark: '120 96 64',
    highlight: '212 168 72',
  },
  cinnabar: {
    id: 'cinnabar',
    name: 'Cinnabar',
    blurb: 'Vermillion red and brass.',
    kind: 'supporter',
    light: '226 178 142',
    dark: '108 44 36',
    highlight: '255 184 76',
  },
  ocean: {
    id: 'ocean',
    name: 'Ocean',
    blurb: 'Pale blue tide, deep navy.',
    kind: 'supporter',
    light: '210 224 234',
    dark: '54 86 124',
    highlight: '108 172 220',
  },
  neon: {
    id: 'neon',
    name: 'Neon',
    blurb: 'Cyber grid, electric blue.',
    kind: 'supporter',
    light: '94 110 140',
    dark: '22 28 44',
    highlight: '88 220 255',
  },
  parchment: {
    id: 'parchment',
    name: 'Parchment',
    blurb: 'Aged paper and ochre ink.',
    kind: 'supporter',
    light: '236 218 178',
    dark: '158 116 70',
    highlight: '226 168 80',
  },
};

export const BOARD_SET_IDS: BoardSetId[] = [
  'classic',
  'walnut',
  'marble',
  'slate',
  'jade',
  'bone',
  'cinnabar',
  'ocean',
  'neon',
  'parchment',
];

export function cssVarsForBoardSet(set: BoardSet): Record<string, string> {
  return {
    '--c-board-light': set.light,
    '--c-board-dark': set.dark,
    '--c-board-hl': set.highlight,
  };
}
